import { useState } from 'react';
import { Box, Button, ButtonGroup, CircularProgress, Dialog, DialogContent, IconButton, MenuItem, Pagination, Select, Stack, Typography } from '@mui/material';
import { Add, Archive, Close } from '@mui/icons-material';
import ProductsTable from './ProductsTable';
import { CreateProductFlow } from '../create/CreateProductFlow';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';

import { useProducts } from '../../hooks/useProducts';
import { Product } from '@domain/product/models/Product';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';

import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import { ProductStatus } from '@domain/product/models/ProductFilter';
import { useConnections } from '@features/connections/generic/hooks/useConnections';
import EmptyState from '@shared/ui/common/EmptyState';
import { MarketplaceGrid } from '@features/connections/generic/components/MarketplaceGrid';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import BulkActionBar from '../bulk-edit/bulkActionBar';
import ArchivedProductsBottomSheet from './ArchivedProductsBottomSheet';
import { Icon } from '@shared/icons';
import UnclassifiedProductsBanner from './UnclassifiedProductsBanner';

const Products = () => {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const { connections } = useConnections();

  const [purchasable, setPurchasable] = useState(true);
  const [createConnectionDialogOpen, setCreateConnectionDialogOpen] = useState(false);
  const { filterCriteria, updateFilter, page, setPage, limit, setLimit } = useProductsFilter(50);

  const { products: productsPage, isLoading, isFetching } = useProducts({ ...filterCriteria, page, limit, purchasable });

  const handleTabChange = (newValue: boolean) => {
    setPurchasable(newValue);
    setPage(1);
  };

  const handleSelectionChange = (selected: Product[]) => {
    setSelectedProducts(selected);
  };

  return (
    <>
      <Stack direction="row" height={48} py={2} alignItems="center" justifyContent="space-between" sx={{ position: 'sticky', top: 0, zIndex: 99, backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider', minWidth: 0, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
          <ButtonGroup variant="outlined">
            <Button
              onClick={() => handleTabChange(true)}
              color='info'
              variant={purchasable ? 'contained' : 'outlined'}
              size='small'
            >
              Purchasable
            </Button>
            <Button
              onClick={() => handleTabChange(false)}
              color='info'
              variant={!purchasable ? 'contained' : 'outlined'}
              size='small'
            >
              Assembled
            </Button>
          </ButtonGroup>

          <Button
            variant="text"
            color="info"
            size="small"
            onClick={() =>
              openBottomSheet({
                title: 'Archived products',
                component: <ArchivedProductsBottomSheet onClose={closeBottomSheet} />,
              })
            }
          >
            Archived
          </Button>

          <ProductsFilter
            onFilterChange={updateFilter}
          />

          <ActiveFilters filterCriteria={filterCriteria} updateFilter={updateFilter} />

        </Stack>


        <Box sx={{ position: 'absolute', right: 0, height: '48px' }}>
          <BulkActionBar
            selectedProducts={selectedProducts}
            products={productsPage?.status === 'success' ? productsPage.data : []}
            setSelectedProducts={setSelectedProducts}
            totalCount={productsPage?.status === 'success' ? productsPage.total : undefined}
            status={ProductStatus.ACTIVE}
            filter={{ ...filterCriteria, page, limit, active: true }}
          />
        </Box>


        {selectedProducts.length === 0 && (
          <Button
            startIcon={<Icon icon="Plus01" />}
            variant="text"
            color='secondary'
            size="small"
            sx={{ flexShrink: 0 }}
            onClick={() => openBottomSheet({ title: 'Create Product', component: <CreateProductFlow onClose={closeBottomSheet} />, size: BottomSheetSize.MD })}
          >
            Create Product
          </Button>
        )}

      </Stack>

      <UnclassifiedProductsBanner />

      {productsPage?.status === 'success' && productsPage.data.length > 0 ? (
        <>
          <ProductsTable
            products={productsPage?.status === 'success' ? productsPage.data : []}
            showCheckboxColumn={true}
            selectedProducts={selectedProducts}
            onSelectionChange={handleSelectionChange}
          />

          <Stack p={2} direction="row" justifyContent="center" alignItems="center">

            <Pagination
              count={productsPage?.totalPages || 0}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="large"
              shape="rounded"
              showFirstButton
              showLastButton
            />

            <Box ml={2}>
              <Select
                value={limit}
                label="Items per page"
                onChange={(e) => setLimit(Number(e.target.value))}
                size="small"
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </Box>
          </Stack>
        </>
      ) : isLoading || isFetching ? (
        <Stack direction="row" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Stack>
      ) : connections?.length === 0 ? (
        <EmptyState
          title="Import your products"
          description="You don't have any connections yet. Import your products from a connection to get started."
          action={{
            label: 'Create Connection',
            onClick: () => setCreateConnectionDialogOpen(true)
          }}
        />
      ) : (
        <EmptyState
          title="No products found"
          description="You don't have any products yet. Create a product to get started."
        />
      )}

      <Dialog open={createConnectionDialogOpen} onClose={() => setCreateConnectionDialogOpen(false)}>
        <DialogHeader>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Add a connection</Typography>
            <IconButton onClick={() => setCreateConnectionDialogOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
        </DialogHeader>
        <DialogContent>
          <Box sx={{ p: 0, pt: 2 }}>
            <MarketplaceGrid onNewConnection={(connection) => setCreateConnectionDialogOpen(false)} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Products; 